import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "components/ui/Button";
import { Loading } from "components/ui/Loading/Loading";
import type { DistributionGroup } from "hooks/use-distribution-api";
import {
  DeliveryTiming,
  type EmailTemplate,
  type EmailTemplateDocuments,
  type UseEmailTemplatesRestApiResult,
} from "hooks/useEmailTemplatesRestApi";
import { type FC, useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import {
  EmailTemplateFormFields,
  type EmailTemplateFormValues,
} from "routes/DeliveryPage/components/EmailTemplateFormFields";
import { z } from "zod";

const emailTemplateFormSchema = z.object({
  id: z.string(),
  name: z.string().trim().min(1, "メールテンプレート名は必須です"),
  from: z.string().min(1, "送信者を選択してください"),
  distributionGroupId: z.string().min(1, "配信先グループを選択してください"),
  subject: z.string().trim().min(1, "件名を入力してください"),
  body: z.string().trim().min(1, "本文を入力してください"),
  includeSender: z.boolean(),
  deliveryTiming: z.enum([DeliveryTiming.Now, DeliveryTiming.Scheduled]),
});

type EmailTemplateModalProps = {
  isOpen: boolean;
  templates: EmailTemplate[];
  distributionGroups: DistributionGroup[];
  selectedTemplateId: string;
  onClose: () => void;
  onSelectTemplate: (template: EmailTemplate) => void;
  onDeleteTemplate: (templateId: string) => void;
  onTemplatesChanged: () => Promise<EmailTemplateDocuments>;
} & Pick<
  UseEmailTemplatesRestApiResult,
  | "createEmailTemplate"
  | "updateEmailTemplate"
  | "deleteEmailTemplate"
  | "createLoading"
  | "updateLoading"
  | "deleteLoading"
>;

const EMPTY_TEMPLATE_FORM_VALUES: EmailTemplateFormValues = {
  id: "",
  name: "",
  from: "",
  distributionGroupId: "",
  subject: "",
  body: "",
  includeSender: false,
  deliveryTiming: DeliveryTiming.Now,
};

const toTemplateFormValues = (
  template: EmailTemplate,
): EmailTemplateFormValues => ({
  id: template.id,
  name: template.name,
  from: template.from,
  distributionGroupId: template.distributionGroupId ?? "",
  subject: template.subject,
  body: template.body,
  includeSender: template.includeSender,
  deliveryTiming: template.deliveryTiming || DeliveryTiming.Now,
});

export const EmailTemplateModal: FC<EmailTemplateModalProps> = ({
  isOpen,
  templates,
  distributionGroups,
  selectedTemplateId,
  onClose,
  onSelectTemplate,
  onDeleteTemplate,
  onTemplatesChanged,
  createEmailTemplate,
  updateEmailTemplate,
  deleteEmailTemplate,
  createLoading: createTemplateLoading,
  updateLoading: updateTemplateLoading,
  deleteLoading: deleteTemplateLoading,
}) => {
  const createMethods = useForm<EmailTemplateFormValues>({
    resolver: zodResolver(emailTemplateFormSchema),
    defaultValues: EMPTY_TEMPLATE_FORM_VALUES,
  });
  const editMethods = useForm<EmailTemplateFormValues>({
    resolver: zodResolver(emailTemplateFormSchema),
    defaultValues: EMPTY_TEMPLATE_FORM_VALUES,
  });

  const [showTemplateCreateForm, setShowTemplateCreateForm] = useState(false);
  const [showTemplateEditForm, setShowTemplateEditForm] = useState(false);
  const [templateModalError, setTemplateModalError] = useState<string | null>(
    null,
  );
  const [editOriginalTemplate, setEditOriginalTemplate] =
    useState<EmailTemplate | null>(null);

  const templateModalLoading =
    createTemplateLoading || updateTemplateLoading || deleteTemplateLoading;

  const resetCreateForm = useCallback(() => {
    setTemplateModalError(null);
    setShowTemplateCreateForm(false);
    createMethods.reset(EMPTY_TEMPLATE_FORM_VALUES);
  }, [createMethods]);

  const resetEditForm = useCallback(() => {
    setTemplateModalError(null);
    setShowTemplateEditForm(false);
    setEditOriginalTemplate(null);
    editMethods.reset(EMPTY_TEMPLATE_FORM_VALUES);
  }, [editMethods]);

  const handleClose = useCallback(() => {
    if (templateModalLoading) return;
    resetCreateForm();
    resetEditForm();
    onClose();
  }, [templateModalLoading, resetCreateForm, resetEditForm, onClose]);

  const handleOpenCreateForm = useCallback(() => {
    setTemplateModalError(null);
    setShowTemplateCreateForm(true);
    setShowTemplateEditForm(false);
    createMethods.reset(EMPTY_TEMPLATE_FORM_VALUES);
  }, [createMethods]);

  const handleOpenEditForm = useCallback(
    (template: EmailTemplate) => {
      setTemplateModalError(null);
      setShowTemplateCreateForm(false);
      setShowTemplateEditForm(true);
      setEditOriginalTemplate(template);
      editMethods.reset(toTemplateFormValues(template));
    },
    [editMethods],
  );

  const handleCreateTemplate = useCallback(async () => {
    await createMethods.handleSubmit(async values => {
      setTemplateModalError(null);
      try {
        const createdTemplate = await createEmailTemplate({
          name: values.name.trim(),
          from: values.from,
          distributionGroupId: values.distributionGroupId,
          subject: values.subject.trim(),
          body: values.body.trim(),
          includeSender: values.includeSender,
          deliveryTiming: values.deliveryTiming,
        });

        await onTemplatesChanged();
        toast.success("メールテンプレートを保存しました");
        resetCreateForm();
        resetEditForm();
        onSelectTemplate(createdTemplate);
        onClose();
      } catch {
        setTemplateModalError("メールテンプレートの保存に失敗しました");
      }
    })();
  }, [
    createMethods,
    createEmailTemplate,
    onTemplatesChanged,
    resetCreateForm,
    resetEditForm,
    onSelectTemplate,
    onClose,
  ]);

  const handleUpdateTemplate = useCallback(async () => {
    await editMethods.handleSubmit(async values => {
      if (!values.id) return;

      setTemplateModalError(null);
      try {
        const updatedTemplate = await updateEmailTemplate(values.id, {
          name: values.name.trim(),
          from: values.from,
          distributionGroupId: values.distributionGroupId,
          subject: values.subject.trim(),
          body: values.body.trim(),
          includeSender: values.includeSender,
          deliveryTiming: values.deliveryTiming,
        });

        const refreshedTemplates = await onTemplatesChanged();
        const refreshedTemplate =
          refreshedTemplates.items.find(t => t.id === updatedTemplate.id) ||
          updatedTemplate;
        toast.success("メールテンプレートを保存しました");
        setEditOriginalTemplate(refreshedTemplate);
        editMethods.reset(toTemplateFormValues(refreshedTemplate));
      } catch {
        setTemplateModalError("メールテンプレートの保存に失敗しました");
      }
    })();
  }, [editMethods, updateEmailTemplate, onTemplatesChanged]);

  const hasEditChanges = useCallback(() => {
    if (!editOriginalTemplate) return true;
    const values = editMethods.getValues();
    return (
      values.name !== (editOriginalTemplate.name || "") ||
      values.from !== (editOriginalTemplate.from || "") ||
      values.distributionGroupId !==
        (editOriginalTemplate.distributionGroupId || "") ||
      values.subject !== (editOriginalTemplate.subject || "") ||
      values.body !== (editOriginalTemplate.body || "") ||
      values.includeSender !== (editOriginalTemplate.includeSender || false) ||
      values.deliveryTiming !==
        (editOriginalTemplate.deliveryTiming || DeliveryTiming.Now)
    );
  }, [editOriginalTemplate, editMethods]);

  const handleApplyTemplate = useCallback(async () => {
    if (hasEditChanges()) {
      setTemplateModalError("未保存の変更があります。先に保存してください");
      return;
    }

    const values = editMethods.getValues();
    const templateToApply =
      templates.find(t => t.id === values.id) || editOriginalTemplate;
    if (!templateToApply) return;

    resetEditForm();
    onSelectTemplate(templateToApply);
    onClose();
  }, [
    hasEditChanges,
    editMethods,
    templates,
    editOriginalTemplate,
    resetEditForm,
    onSelectTemplate,
    onClose,
  ]);

  const handleDeleteTemplate = useCallback(async () => {
    const values = editMethods.getValues();
    if (!values.id) return;

    const targetName = values.name.trim() || "このメールテンプレート";
    if (!window.confirm(`${targetName} を削除しますか？`)) return;

    setTemplateModalError(null);
    try {
      await deleteEmailTemplate(values.id);
      await onTemplatesChanged();
      toast.success("メールテンプレートを削除しました");
      if (selectedTemplateId === values.id) {
        onDeleteTemplate(values.id);
      }
      resetEditForm();
    } catch {
      setTemplateModalError("メールテンプレートの削除に失敗しました");
    }
  }, [
    editMethods,
    deleteEmailTemplate,
    onTemplatesChanged,
    selectedTemplateId,
    onDeleteTemplate,
    resetEditForm,
  ]);

  if (!isOpen) return null;

  return (
    <div className="modal modal-open">
      <div className="modal-box max-w-3xl">
        <div className="flex items-center justify-between">
          <h3 className="font-bold text-lg">メールテンプレート選択</h3>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleClose}
            disabled={templateModalLoading}
          >
            ✕
          </Button>
        </div>

        {templateModalError && (
          <div className="alert alert-error mt-4">
            <span>{templateModalError}</span>
          </div>
        )}

        {!showTemplateCreateForm && !showTemplateEditForm ? (
          <div className="mt-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="text-sm text-base-content/70">
                一覧からメールテンプレートを選択してください
              </div>
              <Button type="button" size="sm" onClick={handleOpenCreateForm}>
                新規登録
              </Button>
            </div>
            <div className="max-h-80 overflow-y-auto border rounded-lg">
              {templates.length === 0 ? (
                <div className="p-4 text-sm text-base-content/60">
                  メールテンプレートがありません
                </div>
              ) : (
                templates.map(t => (
                  <button
                    key={t.id}
                    type="button"
                    className="w-full text-left p-4 border-b last:border-b-0 hover:bg-base-200"
                    onClick={() => handleOpenEditForm(t)}
                  >
                    <div className="font-medium">{t.name}</div>
                    <div className="text-xs text-base-content/60 mt-1">
                      {t.subject}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        ) : showTemplateEditForm ? (
          <div className="mt-4 space-y-4">
            <div className="text-sm text-base-content/70">
              メールテンプレートを編集します
            </div>
            <FormProvider {...editMethods}>
              <EmailTemplateFormFields
                distributionGroups={distributionGroups}
              />
            </FormProvider>
            <div className="flex justify-between gap-2">
              <Button
                type="button"
                variant="destructive"
                isOutline
                onClick={handleDeleteTemplate}
                disabled={templateModalLoading}
              >
                削除
              </Button>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={resetEditForm}
                  disabled={templateModalLoading}
                >
                  戻る
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleUpdateTemplate}
                  disabled={templateModalLoading}
                >
                  保存
                </Button>
                <Button
                  type="button"
                  onClick={handleApplyTemplate}
                  disabled={templateModalLoading}
                >
                  読み込む
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-4 space-y-4">
            <div className="text-sm text-base-content/70">
              新規メールテンプレートを登録します
            </div>
            <FormProvider {...createMethods}>
              <EmailTemplateFormFields
                distributionGroups={distributionGroups}
              />
            </FormProvider>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={resetCreateForm}
                disabled={templateModalLoading}
              >
                戻る
              </Button>
              <Button
                type="button"
                onClick={handleCreateTemplate}
                disabled={templateModalLoading}
              >
                {templateModalLoading ? (
                  <Loading size="sm" />
                ) : (
                  "保存して読み込む"
                )}
              </Button>
            </div>
          </div>
        )}
      </div>
      <div className="modal-backdrop" onClick={handleClose} />
    </div>
  );
};
